import { principlePresentation, type PrincipleSignal } from "@chessla/shared";
import type { CSSProperties } from "react";
import { chessArrow, type ChessArrow, type ChessSquareStyles } from "@/shared/components/chessboard";

type SignalMove = { fenBefore: string; fenAfter: string; principleSignals?: readonly PrincipleSignal[] };

export const visiblePrincipleSignals = (
  move: SignalMove | null,
  displayFen: string,
  revealEvidence: boolean,
  temporaryActive: boolean,
): PrincipleSignal[] => {
  if (!move || !revealEvidence || temporaryActive) return [];
  return displayFen === move.fenBefore || displayFen === move.fenAfter ? [...(move.principleSignals ?? [])] : [];
};

const styleFor = (role: PrincipleSignal["anchors"][number]["role"]): CSSProperties =>
  role === "danger" || role === "target"
    ? { boxShadow: "inset 0 0 0 4px rgba(255,61,18,.9)", backgroundColor: "rgba(255,61,18,.24)" }
    : role === "subject"
      ? { boxShadow: "inset 0 0 0 4px rgba(240,236,226,.92)" }
      : { backgroundImage: "radial-gradient(circle, rgba(240,236,226,.82) 0 18%, transparent 20%)" };

const title = (name: string) => name.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());

export const principleMarkers = (
  signals: readonly PrincipleSignal[],
): { squareStyles: ChessSquareStyles; arrows: ChessArrow[]; description: string } => ({
  squareStyles: Object.fromEntries(
    signals.flatMap((signal) => signal.anchors.map((item) => [item.square, styleFor(item.role)])),
  ),
  arrows: signals.flatMap((signal) =>
    signal.arrows.map((item) =>
      chessArrow(
        item.from,
        item.to,
        item.role === "verified_alternative" || item.role === "support"
          ? "rgba(240,236,226,.95)"
          : "rgba(255,61,18,.95)",
        {
          label: `${signal.status === "missed" ? "Missed" : "Applied"} principle: ${title(principlePresentation(signal.principle).name)}`,
          description: `${principlePresentation(signal.principle).cue} ${item.from} → ${item.to}.`,
        },
      ),
    ),
  ),
  description: signals
    .map(
      (signal) =>
        `${signal.status === "missed" ? "Missed" : "Applied"} principle: ${title(principlePresentation(signal.principle).name)}`,
    )
    .join(". "),
});
